import { AuthnTransaction as OktaAuthTransaction } from '@okta/okta-auth-js';

export enum MfaSignOutReason {
  TIMEOUT = 'TIMEOUT',
  RESEND_EXCEED_LIMIT = 'RESEND_EXCEED_LIMIT',
  CODE_EXCEED_LIMIT = 'INCORRECT_CODE_EXCEED_LIMIT',
}

export enum LoginStatus {
  SUCCESS = 'SUCCESS',
  MFA_REQUIRED = 'MFA_REQUIRED',
  MFA_CHALLENGE = 'MFA_CHALLENGE',
  LOCKED_OUT = 'LOCKED_OUT',
  MFA_ENROLL = 'MFA_ENROLL',
  MFA_ENROLL_ACTIVATE = 'MFA_ENROLL_ACTIVATE',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  PASSWORD_WARN = 'PASSWORD_WARN',
  PASSWORD_EXPIRED = 'PASSWORD_EXPIRED',
}

export enum ViewScreen {
  Login,
  LoginSuccess,
  MFA,
  MfaChallenge,
  MfaEnrollWelcome,
  MfaEnrollVerify,
  MfaEnrollPhone,
  MfaEnrollPhoneConfirm,
  InviteFamily,
  LoginNoEmail,
  TermsUpdated,
  Error,
  PasswordExpire,
}

export const VIEW_SCREEN_URL: Record<ViewScreen, string> = {
  [ViewScreen.Login]: '/public/login',
  [ViewScreen.LoginSuccess]: '/public/login/callback',
  [ViewScreen.InviteFamily]: '/secure/consumer/onboarding/invite-family',
  [ViewScreen.MFA]: '/public/mfa/choose-factor',
  [ViewScreen.MfaChallenge]: '/public/mfa/verify',
  [ViewScreen.MfaEnrollWelcome]: '/public/mfa/enroll',
  [ViewScreen.MfaEnrollVerify]: '/public/login/enroll/challenge',
  [ViewScreen.MfaEnrollPhone]: '/public/login/enroll/phone-number',
  [ViewScreen.MfaEnrollPhoneConfirm]:
    '/public/login/enroll/phone-number-confirm',
  [ViewScreen.LoginNoEmail]: '/public/login/no-email',
  [ViewScreen.TermsUpdated]: '/public/login/terms',
  [ViewScreen.Error]: '/public/login/error',
  [ViewScreen.PasswordExpire]: '/public/forgot-password/reset-password',
};

export type FactorType = 'sms' | 'email';
export type FactorEnrollStatus = 'NOT_SETUP' | 'ACTIVE';

export interface AuthTransaction extends OktaAuthTransaction {
  cancel: () => Promise<AuthTransaction>;
  prev: () => Promise<AuthTransaction>;
}

export type AuthTransactionSuccess = AuthTransaction & {
  status: LoginStatus.SUCCESS;
};

export type AuthTransactionUnauth = AuthTransaction & {
  status: LoginStatus.UNAUTHENTICATED;
};

export type AuthTransactionSuccessAuthN = AuthTransactionSuccess & {
  sessionToken: string;
};

export type AuthTransactionSuccessStepUp = AuthTransactionSuccess & {
  type: 'SESSION_STEP_UP';
  data: {
    _links: {
      next: {
        href: string;
      };
    };
  };
};

export type AuthTransactionUnauthStepUp = AuthTransactionUnauth & {
  type: 'SESSION_STEP_UP';
  data: { stateToken?: string };
  authentication: {
    client: {
      id: string;
      name: string;
      uri: string;
    };
    issuer: {
      id: string;
      name: string;
      uri: string;
    };
    request: {
      redirect_uri: string;
      response_mode: string;
      response_type: string;
      scope: string;
      state: string;
    };
    protocol: string;
  };
};

export interface AuthTransactionMfaChallenge extends AuthTransaction {
  status: LoginStatus.MFA_CHALLENGE;
  factor: Factor;
  verify: ({
    passCode,
    rememberDevice,
  }: {
    passCode: string;
    rememberDevice: boolean;
  }) => Promise<AuthTransaction>;
  resend: (string: FactorType) => Promise<AuthTransaction>;
}

export interface AuthTransactionMfaEnroll extends AuthTransaction {
  status: LoginStatus.MFA_ENROLL;
  factors: FactorEnroll[];
  skip: () => Promise<AuthTransaction>;
}

export interface AuthTransactionMfaEnrollActivate extends AuthTransaction {
  status: LoginStatus.MFA_ENROLL_ACTIVATE;
  factor: FactorEnroll;
  activate: ({ passCode }: { passCode: string }) => Promise<AuthTransaction>;
  resend: () => Promise<AuthTransaction>;
}

export interface AuthTransactionMfaEnrollSuccess extends AuthTransaction {
  status: LoginStatus.SUCCESS;
  skip: () => Promise<AuthTransaction>;
}

export interface AuthTransactionMfaRequired extends AuthTransaction {
  status: LoginStatus.MFA_REQUIRED;
  factors: FactorVerify[];
  user: { profile: { login: string } };
}

export interface Factor {
  factorType: FactorType;
  id: string;
  profile: {
    email?: string;
    phoneNumber?: string;
  };
}

export interface FactorVerify extends Factor {
  verify: () => Promise<AuthTransaction>;
}

export interface FactorEnroll extends Factor {
  enroll: (profile?: Profile) => Promise<AuthTransaction>;
  status: FactorEnrollStatus;
}

interface Profile {
  profile: {
    phoneNumber: string;
    updatePhone: boolean;
  };
}

export enum SignInFailReason {
  Unknown = 0,
  InvalidCredentials = 1,
  LockedOut = 2,
  VerifyFailed = 3,
  TokenExpired = 4,
  NotFound = 5,
  SmsLimit = 6,
  SmsAlreadySent = 7,
  InlineHookFailed = 8,
  AccessDenied = 9,
  Error403 = 10,
  ConnectionReset = 11,
  PasswordWarn = 12,
  PasswordExpire = 13,
  ExceedLimit = 14,
  InvalidMobile = 15,
  EmailPasscodeAlreadySent = 16,
  TooManyRequest = 17,
  TimeOut = 18,
}

export interface SignInOptions {
  username: string;
  password: string;
  tokenText?: string;
}

export enum LoginClient {
  CignaForBrokers = 1,
  Seamless,
}

export interface CpiValidateToken {
  tokenInfo: {
    isTokenValid: boolean;
    isTermedUser: boolean;
    isFutureUser: boolean;
    isEAPUser: boolean;
    structIdLevel3?: string;
  };
}

export interface CpiBrandingInfo {
  brandType: 'CIGNA' | 'CAREALLIES';
}

export interface UserAgreementResponse {
  userAgreement: {
    currentVersion: string;
    versionToAccept: string;
    agreementIndicator: 'Y' | 'N';
  };
}

export interface ResetPasswordOptions {
  oldPassword: string;
  newPassword: string;
}

export enum ResetPasswordFailReason {
  Unknown = 0,
  TokenExpired = 1,
  UpdateFailed = 2,
  UpdateCurrentPasswordFailed = 3,
  OldPasswordError = 4,
  RecentPasswordError = 5,
}
